"use client";
import type { ISchedule } from "@gettactic/api";
import {
  formatScheduleTimeRange,
  getDateFromSchedule
} from "@gettactic/helpers/src/date-time/getTimeFromSchedule";
import { format, parseISO } from "date-fns";
import { Repeat } from "lucide-react";
import React from "react";
import {
  describeRecurrence,
  type ResourceReservationGroup
} from "./groupResourceReservations";

/** How far ahead we look for bookings that dedicating would cancel. */
export const DEDICATE_LOOKAHEAD_DAYS = 365;

// Schedule datetimes are office wall-clock with a cosmetic trailing `Z` (see
// `formatApiDatetime`), so they are already in the office's own time —
// converting them would add the viewer's offset on top of the office's.
const formatDay = (schedule: ISchedule): string =>
  format(
    parseISO(`${getDateFromSchedule(schedule.start)}T00:00:00`),
    "EEE, MMM d"
  );

const getBookedBy = (schedule: ISchedule): string =>
  schedule.slots?.[0]?.user?.name ||
  schedule.organizer?.name ||
  schedule.user?.name ||
  "Someone";

interface ReservationRowProps {
  group: ResourceReservationGroup;
}

const ReservationRow: React.FC<ReservationRowProps> = ({ group }) => {
  if (group.kind === "single") {
    return (
      <li className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
        <span className="truncate font-medium text-neutral-900">
          {getBookedBy(group.schedule)}
        </span>
        <span className="flex-none text-neutral-500 text-xs">
          {formatDay(group.schedule)}, {formatScheduleTimeRange(group.schedule)}
        </span>
      </li>
    );
  }

  // A series is one line, not one line per occurrence: the pattern is what the
  // admin needs to judge the impact, and cancelling it removes every
  // occurrence — including any beyond the window we looked at.
  const pattern = describeRecurrence(group.first);
  return (
    <li className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
      <span className="flex min-w-0 items-baseline gap-1.5">
        <Repeat className="size-3 flex-none translate-y-px text-neutral-400" />
        <span className="truncate font-medium text-neutral-900">
          {getBookedBy(group.first)}
        </span>
      </span>
      <span className="flex-none text-right text-neutral-500 text-xs">
        <span className="block">
          {pattern ??
            `Repeats · ${group.schedules.length} upcoming reservation${
              group.schedules.length === 1 ? "" : "s"
            }`}
        </span>
        <span className="block">
          from {formatDay(group.first)}, {formatScheduleTimeRange(group.first)}
        </span>
      </span>
    </li>
  );
};

export interface DedicateResourceWarningProps {
  resourceName: string;
  groups: ResourceReservationGroup[];
  isLoading: boolean;
  /** Set when the reservations could not be loaded. */
  error: unknown;
  /** True when there were more reservations than we could enumerate. */
  isTruncated: boolean;
}

/**
 * Body of the "make this a dedicated desk" confirmation.
 *
 * A dedicated desk is nobody else's to book, so any reservation already on it
 * has to go. This names them before the admin agrees, rather than cancelling
 * other people's bookings silently.
 */
export const DedicateResourceWarning: React.FC<
  DedicateResourceWarningProps
> = ({ resourceName, groups, isLoading, error, isTruncated }) => {
  if (isLoading) {
    return (
      <p className="text-tertiary">
        Checking <strong>{resourceName}</strong> for existing reservations…
      </p>
    );
  }

  if (error || isTruncated) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-destructive">
          {error
            ? `Could not check ${resourceName} for existing reservations.`
            : `${resourceName} has too many reservations to list.`}
        </p>
        <p className="text-tertiary">
          Dedicating it now would cancel reservations without showing you which
          ones. Close this and try again.
        </p>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <p className="text-tertiary">
        Make <strong>{resourceName}</strong> a dedicated desk? It will belong to
        one person and nobody else will be able to book it.
      </p>
    );
  }

  const seriesCount = groups.filter((g) => g.kind === "recurring").length;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-tertiary">
        <strong>{resourceName}</strong> has {groups.length} upcoming reservation
        {groups.length === 1 ? "" : "s"}
        {seriesCount > 0 &&
          ` (${seriesCount} recurring — every occurrence will be cancelled, including any beyond the next year)`}
        . Dedicating it will cancel {groups.length === 1 ? "it" : "them all"} —
        the people below will lose their booking.
      </p>
      <ul className="max-h-56 divide-y divide-neutral-100 overflow-y-auto rounded-md border border-neutral-200">
        {groups.map((group) => (
          <ReservationRow key={group.id} group={group} />
        ))}
      </ul>
    </div>
  );
};
